/**
 * Product price engine (mirror of backend/services/product_price.py — keep the two in sync).
 *
 * The field the user typed is the source of truth and is never altered:
 *   basis 'incl': incl = typed; vat = round_half_up(incl * rate / (100 + rate)); excl = incl - vat
 *   basis 'excl': excl = typed; incl = round_half_up(excl * (100 + rate) / 100); vat = incl - excl
 * so excl + vat === incl always. All maths is on integer cents (BTW rates are whole percentages: 9 or 21).
 */

/** "5.99" -> 599. Exact string parsing (no float multiplication); half-up beyond 2 decimals. Returns null if not a number. */
export function toCents(value) {
    const text = String(value ?? '').trim().replace(',', '.');
    const m = /^(\d*)(?:\.(\d*))?$/.exec(text);
    if (!m || (m[1] === '' && !m[2])) return null;
    const frac = `${m[2] || ''}000`;
    return parseInt(m[1] || '0', 10) * 100 + parseInt(frac.slice(0, 2), 10) + (parseInt(frac[2], 10) >= 5 ? 1 : 0);
}

/** 599 -> "5.99" */
export function formatCents(cents) {
    const c = Math.round(cents);
    return `${Math.floor(c / 100)}.${String(c % 100).padStart(2, '0')}`;
}

/** cents = typed price in integer cents -> { incl, excl, vat } in integer cents. */
export function splitPrice(cents, rate, basis = 'incl') {
    const r = Number(rate) || 0;
    if (basis === 'excl') {
        const incl = Math.floor((2 * cents * (100 + r) + 100) / 200);
        return { incl, excl: cents, vat: incl - cents };
    }
    const vat = r === 0 ? 0 : Math.floor((2 * cents * r + (100 + r)) / (2 * (100 + r)));
    return { incl: cents, excl: cents - vat, vat };
}

/**
 * Form helper: the typed field (string) + basis -> the three values as strings, or null for empty/invalid input.
 * The typed field is returned exactly as normalised cents, never recomputed from the other value.
 */
export function priceFields(typed, rate, basis = 'incl') {
    const cents = toCents(typed);
    if (cents === null) return null;
    const p = splitPrice(cents, rate, basis);
    return { incl: formatCents(p.incl), excl: formatCents(p.excl), vat: formatCents(p.vat) };
}
